import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { getCrawlerSchedules, putCrawlerSchedule } from "../../services/CrawlerScheduleService";

const ALL_HOURS = Array.from({ length: 24 }, (_, i) => i);
const formatHourShort = (h: number) => (h === 0 ? "12a" : h < 12 ? `${h}` : h === 12 ? "12p" : `${h - 12}`);
const formatHourLong = (h: number) => {
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
};

const RETAILERS = [
  { key: "walmart", name: "Walmart", dot: "bg-blue-600", cell: "bg-blue-600 hover:bg-blue-700" },
  { key: "homeDepot", name: "Home Depot", dot: "bg-orange-500", cell: "bg-orange-500 hover:bg-orange-600" },
  { key: "target", name: "Target", dot: "bg-red-500", cell: "bg-red-500 hover:bg-red-600" },
  { key: "dollarGeneral", name: "Dollar General", dot: "bg-amber-400", cell: "bg-amber-400 hover:bg-amber-500" },
  { key: "bestBuy", name: "Best Buy", dot: "bg-indigo-600", cell: "bg-indigo-600 hover:bg-indigo-700" },
  { key: "costco", name: "Costco", dot: "bg-emerald-600", cell: "bg-emerald-600 hover:bg-emerald-700" },
] as const;

type RetailerKey = (typeof RETAILERS)[number]["key"];
type Schedule = Record<RetailerKey, Set<number>>;

const mkEmptySchedule = (): Schedule => ({
  walmart: new Set(),
  homeDepot: new Set(),
  target: new Set(),
  dollarGeneral: new Set(),
  bestBuy: new Set(),
  costco: new Set(),
});

export default function CrawlerScheduleSettings() {
  const queryClient = useQueryClient();
  const [schedule, setSchedule] = useState<Schedule>(() => mkEmptySchedule());
  const [dirty, setDirty] = useState<Set<RetailerKey>>(new Set());

  const { isLoading, data, dataUpdatedAt } = useQuery({
    queryKey: ["crawler-schedules"],
    queryFn: getCrawlerSchedules,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!data) return;
    const s = mkEmptySchedule();
    for (const item of data) {
      if (item.retailer in s) {
        s[item.retailer as RetailerKey] = new Set(item.schedule ?? []);
      }
    }
    setSchedule(s);
    setDirty(new Set());
  }, [data, dataUpdatedAt]);

  const saveMutation = useMutation({
    mutationFn: ({ retailer, hours }: { retailer: string; hours: number[] }) =>
      putCrawlerSchedule(retailer, hours),
  });

  const updateSchedule = (retailer: RetailerKey, next: Set<number>) => {
    setSchedule((prev) => ({ ...prev, [retailer]: next }));
    setDirty((prev) => new Set(prev).add(retailer));
  };

  const toggle = (retailer: RetailerKey, hour: number) => {
    const next = new Set(schedule[retailer]);
    if (next.has(hour)) next.delete(hour);
    else next.add(hour);
    updateSchedule(retailer, next);
  };

  const setAll = (retailer: RetailerKey) => updateSchedule(retailer, new Set(ALL_HOURS));
  const clearAll = (retailer: RetailerKey) => updateSchedule(retailer, new Set());

  const saveAll = async () => {
    if (dirty.size === 0) return;
    const retailers = [...dirty];
    try {
      for (const retailer of retailers) {
        await saveMutation.mutateAsync({
          retailer,
          hours: [...schedule[retailer]].sort((a, b) => a - b),
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["crawler-schedules"] });
      setDirty(new Set());
    } catch {
      /* leave dirty so user can retry */
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-dark-400" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-3">
        <div className="leading-tight">
          <h2 className="text-[13px] font-bold text-gray-900 dark:text-white">Crawler schedule</h2>
          <p className="text-[11px] text-gray-400 dark:text-dark-400">Store-local hours each crawler runs. Click a cell to toggle it; crawlers pick up changes on their next run.</p>
        </div>
        <button
          type="button"
          onClick={() => void saveAll()}
          disabled={dirty.size === 0 || saveMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          {saveMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {dirty.size > 0 ? `Save ${dirty.size} change${dirty.size > 1 ? "s" : ""}` : "Saved"}
        </button>
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto pb-4">
        <div className="min-w-[720px] px-4">
          <div className="grid gap-y-1" style={{ gridTemplateColumns: "120px repeat(24, minmax(0, 1fr)) 52px" }}>
            {/* Hour header row */}
            <div />
            {ALL_HOURS.map((h) => (
              <div
                key={`h-${h}`}
                className={`text-center text-[10px] font-mono pb-1 ${h === 0 || h === 12 ? "font-bold text-gray-500 dark:text-dark-200" : "text-gray-400 dark:text-dark-400"} ${h === 12 ? "border-l border-gray-200 dark:border-dark-600" : ""}`}
              >
                {formatHourShort(h)}
              </div>
            ))}
            <div />

            {/* Retailer rows */}
            {RETAILERS.map(({ key, name, dot, cell }) => {
              const selected = schedule[key];
              const allSelected = selected.size === 24;
              const isDirty = dirty.has(key);
              return (
                <div key={key} className="contents">
                  <div className="flex items-center gap-2 pr-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                    <span className="text-xs font-semibold text-gray-800 dark:text-dark-50 truncate">{name}</span>
                    <span className="text-[10px] font-mono tabular-nums text-gray-400 dark:text-dark-400 ml-auto">
                      {selected.size}
                    </span>
                    {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />}
                  </div>
                  {ALL_HOURS.map((h) => {
                    const isActive = selected.has(h);
                    return (
                      <button
                        type="button"
                        key={`${key}-${h}`}
                        onClick={() => toggle(key, h)}
                        title={`${name} · ${formatHourLong(h)}${isActive ? " (on)" : ""}`}
                        className={`h-7 mx-px rounded transition-colors ${h === 12 ? "border-l border-gray-200 dark:border-dark-600" : ""} ${isActive
                          ? cell
                          : "bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600"
                          }`}
                      />
                    );
                  })}
                  <div className="flex items-center justify-end pl-2">
                    <button
                      type="button"
                      onClick={() => (allSelected ? clearAll(key) : setAll(key))}
                      className="text-[11px] font-semibold px-2 py-1 rounded-md text-gray-400 dark:text-dark-400 hover:text-gray-700 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                    >
                      {allSelected ? "Clear" : "All"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* AM / PM footer hint */}
          <div className="grid mt-1.5" style={{ gridTemplateColumns: "120px repeat(24, minmax(0, 1fr)) 52px" }}>
            <div />
            <div className="col-span-12 text-center text-[10px] font-bold uppercase tracking-[0.16em] text-gray-300 dark:text-dark-500">AM</div>
            <div className="col-span-12 text-center text-[10px] font-bold uppercase tracking-[0.16em] text-gray-300 dark:text-dark-500 border-l border-gray-200 dark:border-dark-600">PM</div>
            <div />
          </div>
        </div>
      </div>
    </div>
  );
}
